import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import PasswordInput from '../components/auth/PasswordInput.jsx';

const LOCKOUT_KEY = 'bos.lockoutUnlockAt';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Lockout state — countdown to lockoutUntil (ISO string).
  const [lockoutUntil, setLockoutUntil] = useState(() => {
    try {
      const raw = sessionStorage.getItem(LOCKOUT_KEY);
      if (!raw) return null;
      const t = new Date(raw);
      if (t > new Date()) return raw;
      sessionStorage.removeItem(LOCKOUT_KEY);
      return null;
    } catch { return null; }
  });
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!lockoutUntil) return;
    const tick = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (new Date(lockoutUntil).getTime() <= t) {
        setLockoutUntil(null);
        try { sessionStorage.removeItem(LOCKOUT_KEY); } catch { /* */ }
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [lockoutUntil]);

  // Clear errors as the user starts editing again — feels less sticky.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (error) setError(null); /* eslint-disable-next-line */ }, [email, password]);

  async function onSubmit(e) {
    e.preventDefault();
    if (lockoutUntil) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await login(email, password);
      if (result.mfaRequired) {
        navigate('/mfa', { state: { challengeId: result.challengeId, from } });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;
      if (status === 423 && data?.details?.unlock_at) {
        const until = data.details.unlock_at;
        setLockoutUntil(until);
        try { sessionStorage.setItem(LOCKOUT_KEY, until); } catch { /* */ }
        setError(data.message || 'Account locked');
      } else {
        setError(data?.message || 'Login failed');
      }
    } finally {
      setSubmitting(false);
    }
  }

  function ssoLogin() {
    window.location.href = (import.meta.env.VITE_API_BASE_URL || '/api') + '/auth/saml/login';
  }

  const remainingMs = lockoutUntil ? new Date(lockoutUntil).getTime() - now : 0;
  const remainingMin = Math.max(0, Math.floor(remainingMs / 60_000));
  const remainingSec = Math.max(0, Math.floor((remainingMs % 60_000) / 1000));

  return (
    <div className="centered-screen">
      <form className="card auth-form" onSubmit={onSubmit}>
        <h1>Sign in to BOS+</h1>
        <p className="muted">Business Operating System Plus</p>

        <label>
          Email
          <input
            type="email"
            name="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={!!lockoutUntil}
          />
        </label>
        <label>
          Password
          <PasswordInput
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={!!lockoutUntil}
          />
        </label>

        {lockoutUntil && (
          <div className="alert error" role="alert" data-testid="lockout-banner">
            Account locked. Try again in {remainingMin}m {String(remainingSec).padStart(2, '0')}s.
          </div>
        )}
        {error && !lockoutUntil && <div className="alert error" role="alert">{error}</div>}

        <button type="submit" className="btn primary" disabled={submitting || !!lockoutUntil}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        <div className="divider"><span>or</span></div>

        <button type="button" className="btn secondary" onClick={ssoLogin} disabled={!!lockoutUntil}>
          Continue with SSO
        </button>

        <div className="auth-links">
          <Link to="/forgot-password">Forgot password?</Link>
        </div>
      </form>
    </div>
  );
}
